import { stegaClean } from "@sanity/client/stega";

export interface NumberSpec {
  prefix: string;
  suffix: string;
  target: number;
  decimals: number;
  grouped: boolean;
  /** German copy writes "0,5 %"; the comma must survive the count-up. */
  decimal: "." | ",";
}

/**
 * "$100,000" → grouped thousands; "0,5%" → a decimal comma, not a thousands
 * group. Anything without digits returns null and is rendered untouched.
 */
export function parseNumber(raw?: string): NumberSpec | null {
  const clean = (stegaClean(raw) ?? "").trim();
  const match = clean.match(/^([^\d]*)(\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:[.,]\d+)?)([^\d]*)$/);
  if (!match) return null;
  const [, prefix, num, suffix] = match;
  const grouped = /^\d{1,3}(?:,\d{3})+/.test(num);
  const decimal: "." | "," = !grouped && num.includes(",") ? "," : ".";
  const normalized = grouped ? num.replace(/,/g, "") : num.replace(",", ".");
  const target = parseFloat(normalized);
  if (!Number.isFinite(target)) return null;
  return {
    prefix,
    suffix,
    target,
    decimals: (normalized.split(".")[1] ?? "").length,
    grouped,
    decimal,
  };
}

export function formatNumber(spec: NumberSpec, value: number) {
  const digits = spec.grouped
    ? value.toLocaleString("en-US", {
        minimumFractionDigits: spec.decimals,
        maximumFractionDigits: spec.decimals,
      })
    : value.toFixed(spec.decimals).replace(".", spec.decimal);
  return `${spec.prefix}${digits}${spec.suffix}`;
}
